import { useState } from "react";
import { z } from "zod";
import { IconUserPlus } from "@tabler/icons-react";
import { Button, Card, Checkbox, Group, Input, Stack, Text } from "@/components/ui";
import type { Area, OrganizacionAdmin } from "@/lib/types";
import { useCrearUsuario } from "../hooks/use-usuarios-mutations";

const schema = z.object({
  email: z.string().email("Correo inválido"),
  password: z.string().min(8, "Mínimo 8 caracteres"),
});

/** Alterna un valor en un arreglo (selección múltiple por checkbox). */
function toggle(lista: string[], valor: string): string[] {
  return lista.includes(valor) ? lista.filter((v) => v !== valor) : [...lista, valor];
}

/**
 * Alta de usuario · crea la cuenta Identity (email + contraseña temporal) y le
 * asigna roles, áreas y organizaciones en una sola operación. Mismo patrón de
 * "modo crear" que el gestor de Proveedores.
 */
export function CrearUsuarioForm({
  roles,
  areas,
  organizaciones,
  onSalir,
}: {
  roles: string[];
  areas: Area[];
  organizaciones: OrganizacionAdmin[];
  onSalir: () => void;
}) {
  const [email, setEmail] = useState("");
  const [fullName, setFullName] = useState("");
  const [password, setPassword] = useState("");
  const [rolesSel, setRolesSel] = useState<string[]>([]);
  const [areasSel, setAreasSel] = useState<string[]>([]);
  const [orgsSel, setOrgsSel] = useState<string[]>([]);
  const [errors, setErrors] = useState<{ email?: string; password?: string }>({});

  const crear = useCrearUsuario({ onCreado: onSalir });

  const enviar = (e: React.FormEvent) => {
    e.preventDefault();
    const parsed = schema.safeParse({ email, password });
    if (!parsed.success) {
      const errs: typeof errors = {};
      for (const i of parsed.error.issues) errs[i.path[0] as "email" | "password"] = i.message;
      setErrors(errs);
      return;
    }
    setErrors({});
    crear.mutate({
      email: email.trim(),
      fullName: fullName.trim() || null,
      password,
      roles: rolesSel,
      areaIds: areasSel,
      organizacionIds: orgsSel,
    });
  };

  return (
    <Card padding="lg" className="mb-4">
      <form onSubmit={enviar}>
        <Stack gap="md">
          <Group gap="xs" align="center">
            <IconUserPlus size={18} className="text-multiva-600" />
            <Text fw={600}>Nuevo usuario</Text>
          </Group>

          <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
            <Input
              label="Correo electrónico"
              placeholder="nombre@proveedores.test"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              error={errors.email}
              autoComplete="off"
            />
            <Input label="Nombre completo" placeholder="Opcional" value={fullName} onChange={(e) => setFullName(e.target.value)} />
            <Input
              type="password"
              label="Contraseña temporal"
              placeholder="Mínimo 8 caracteres"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              error={errors.password}
              autoComplete="new-password"
            />
          </div>

          <SelectorCheckboxes titulo="Roles" vacio="Sin roles disponibles.">
            {roles.map((r) => (
              <Checkbox key={r} label={r} checked={rolesSel.includes(r)} onCheckedChange={() => setRolesSel((s) => toggle(s, r))} />
            ))}
          </SelectorCheckboxes>

          <SelectorCheckboxes titulo="Áreas" vacio="Sin áreas.">
            {areas.map((a) => (
              <Checkbox key={a.id} label={a.nombre} checked={areasSel.includes(a.id)} onCheckedChange={() => setAreasSel((s) => toggle(s, a.id))} />
            ))}
          </SelectorCheckboxes>

          <SelectorCheckboxes titulo="Organizaciones" vacio="Sin organizaciones.">
            {organizaciones.map((o) => (
              <Checkbox
                key={o.id}
                label={`${o.nombre} · ${o.numeroSociedad}`}
                checked={orgsSel.includes(o.id)}
                onCheckedChange={() => setOrgsSel((s) => toggle(s, o.id))}
              />
            ))}
          </SelectorCheckboxes>

          <Group justify="flex-end" gap="xs">
            <Button type="button" variant="default" onClick={onSalir} disabled={crear.isPending}>
              Cancelar
            </Button>
            <Button type="submit" loading={crear.isPending} leftSection={<IconUserPlus size={14} />}>
              Crear usuario
            </Button>
          </Group>
        </Stack>
      </form>
    </Card>
  );
}

function SelectorCheckboxes({ titulo, vacio, children }: { titulo: string; vacio: string; children: React.ReactNode[] }) {
  return (
    <div>
      <Text size="xs" fw={600} c="dimmed" className="mb-1.5 uppercase tracking-wide">
        {titulo}
      </Text>
      {children.length === 0 ? (
        <Text size="sm" c="dimmed">
          {vacio}
        </Text>
      ) : (
        <div className="flex flex-wrap gap-x-5 gap-y-2 rounded-md border border-border bg-muted/30 p-3">{children}</div>
      )}
    </div>
  );
}
